import { Button, Spinner } from "react-bootstrap";
import styled from "styled-components";

import { LicenceType } from "@evr/constant";
import { Currency, PricingRule, SoftwareLicence } from "@evr/types";
import { Typography } from "@evr/ui/Typography";

import "./branding.css";
import { branding } from "./branding";
import SingleUserPricing from "./SingleUserPricing";

const Container = styled.div`
  display: flex;
  flex-direction: column;
  background-color: white;
  border-right: 1px solid #ccc;
  border-left: 1px solid #ccc;
  height: 100%;
  width: 325px;
  gap: 1rem;
`;

const LicenceCardHeader = styled.div`
  border-bottom: 1px solid #ccc;
  text-align: center;
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const LicenceCardContent = styled.div`
  display: flex;
  flex-direction: column;
  padding: 1rem 0;
  gap: 1rem;
  background-color: white;
  flex-grow: 1;
`;

const LicenceCardFooter = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  padding: 1rem;
`;

const LicenceCardPricing = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
`;

const LicenceFeatures = styled.div`
  display: flex;
  flex-grow: 1;
`;

const getBulletColor = (color?: string) => {
  switch (color) {
    case "gold":
      return "#c7a624";
    case "silver":
      return "#7a7a7a";
    case "bronze":
      return "#cd7f32";
    default:
      return color || "black"; // fallback to passed color or black
  }
};

interface ListItemProps {
  bulletColor?: string;
}

const StyledListItem = styled.li<ListItemProps>`
  font-size: 1.2em;
  list-style-type: disc;

  &::marker {
    color: ${({ bulletColor }) => getBulletColor(bulletColor)};
  }
`;

interface LicenceCardProps {
  licence: SoftwareLicence;
  transactionCurrency: Currency;
  onSubscribe: (licence: SoftwareLicence) => void;
  loading: boolean;
}

const LicenceCard = ({ licence, transactionCurrency, onSubscribe, loading }: LicenceCardProps) => {
  const name = licence.name.replace(/\s*\(.*?\)\s*/g, "").trim();
  const brand = branding.find(b => licence.name.toLowerCase().includes(b.name.toLowerCase()));

  const getPricing = (licence: SoftwareLicence, currency: Currency, pricingRules?: PricingRule[]) => {
    if (licence.licenceType === LicenceType.Trial) {
      return (
        <Typography size={1.2} fontWeight={400}>
          Free
        </Typography>
      );
    }
    if (!pricingRules || pricingRules.length === 0) {
      return <span>Pricing Not Available</span>;
    }

    if (licence.licenceType === LicenceType.SingleUser) {
      return (
        <SingleUserPricing
          licenceType={licence.licenceType}
          transactionCurrency={currency}
          pricingRules={pricingRules!}
        />
      );
    }
  };

  const featureFontSize: number = 0.71;
  const featureFontWeight = 200;

  return (
    <Container className="bg-light" key={licence.softwareLicenceId}>
      <LicenceCardHeader style={{ height: "50px" }} className={brand?.className}>
        <Typography fontWeight={400} size={1.3} className="mb-0">
          {name}
        </Typography>
      </LicenceCardHeader>
      <LicenceCardContent className="bg-light">
        <LicenceFeatures>
          <ul className={`${name.toLowerCase()}-bullets`}>
            <StyledListItem bulletColor={name.toLowerCase()}>
              <Typography fontWeight={featureFontWeight} size={featureFontSize} className="mb-0">
                Maximum number of stored depots: {licence.licenceFeatures?.storedDepotsLimit ?? "Unlimited"}
              </Typography>
            </StyledListItem>
            <StyledListItem bulletColor={name.toLowerCase()}>
              <Typography fontWeight={featureFontWeight} size={featureFontSize} className="mb-0">
                Maximum number of drivers per calculation: {licence.licenceFeatures?.driversPerCalcLimit ?? "Unlimited"}
              </Typography>
            </StyledListItem>
            <StyledListItem bulletColor={name.toLowerCase()}>
              <Typography fontWeight={featureFontWeight} size={featureFontSize} className="mb-0">
                Maximum number of parcel uploads per calculation:{" "}
                {licence.licenceFeatures?.parcelUploadsPerCalcLimit ?? "Unlimited"}
              </Typography>
            </StyledListItem>
            <StyledListItem bulletColor={name.toLowerCase()}>
              <Typography fontWeight={featureFontWeight} size={featureFontSize} className="mb-0">
                Maximum number of geolocations per month:{" "}
                {licence.licenceFeatures?.monthlyGeoLocationsLimit ?? "Unlimited"}
              </Typography>
            </StyledListItem>
            <StyledListItem bulletColor={name.toLowerCase()}>
              <Typography fontWeight={featureFontWeight} size={featureFontSize} className="mb-0">
                Maximum number of calculations stored: {licence.licenceFeatures?.calculationStorageLimit ?? "Unlimited"}
              </Typography>
            </StyledListItem>
            <StyledListItem bulletColor={name.toLowerCase()}>
              <Typography fontWeight={featureFontWeight} size={featureFontSize} className="mb-0">
                Calculations stored for:{" "}
                {licence.licenceFeatures?.calculationStoragePeriod
                  ? `${licence.licenceFeatures.calculationStoragePeriod.value} ${
                      licence.licenceFeatures.calculationStoragePeriod.value === 1
                        ? licence.licenceFeatures.calculationStoragePeriod.unit
                        : licence.licenceFeatures.calculationStoragePeriod.unit + "s"
                    }`
                  : "Unlimited"}
              </Typography>
            </StyledListItem>
            <StyledListItem bulletColor={name.toLowerCase()}>
              <Typography fontWeight={featureFontWeight} size={featureFontSize} className="mb-0">
                Assertiveness:{" "}
                {licence.licenceFeatures?.driverAssertivenessHoursCustomisable === false ? "Fixed" : "Customisable"}
              </Typography>
            </StyledListItem>
            <StyledListItem bulletColor={name.toLowerCase()}>
              <Typography fontWeight={featureFontWeight} size={featureFontSize} className="mb-0">
                Working hours:{" "}
                {licence.licenceFeatures?.driverWorkingHoursCustomisable === false ? "Fixed" : "Customisable"}
              </Typography>
            </StyledListItem>
          </ul>
        </LicenceFeatures>
        <LicenceCardPricing className="bg-light">
          {loading ? <Spinner /> : getPricing(licence, transactionCurrency, licence.pricingRules)}
        </LicenceCardPricing>
      </LicenceCardContent>
      <LicenceCardFooter className={`${brand?.className}-footer`}>
        <Button disabled={loading} onClick={() => onSubscribe(licence)}>
          {loading ? <Spinner /> : <span>Subscribe</span>}
        </Button>
      </LicenceCardFooter>
    </Container>
  );
};

export default LicenceCard;
